import "pixi";
import "phaser";

import * as cursor from "../cursor";
import * as Level from "../leveljson";
import * as player from "../player";
import * as platform from "../platform";
import * as goal from "../goal";
import * as i18n from "../i18n";
import * as laser from "../laser";
import * as logging from "../logging";
import * as tutorial from "../tutorial";
import * as cutandslide from "../cutandslide";
import assets from "../assets";
import * as progression from "../progression";
import * as overlay from "../overlay";
import * as dashboard from "../dashboard";
import LevelSelectState from "./level_select";
import MuteButton from "../ui/mute";
import AudioEngine from "../audio_engine";
import Fog from "../fog";

const BACKGROUNDS = ["background2", "background3", "background5", "background6", "background7"];

function bgForLevel(index: number): string {
    let choice = BACKGROUNDS[Math.floor(BACKGROUNDS.length * Math.min(index, 19) / 20)];
    if (index >= 20) choice = "background8";
    return choice;
}

class PlayerMoveUndo {
    playerJson: any;
    platformName: string | null;
    lastPlatformName: string | null;
    isSafe: boolean;

    constructor(playerJson: any, platformName: string | null, lastPlatformName: string | null, isSafe: boolean) {
        this.playerJson = playerJson;
        this.platformName = platformName;
        this.lastPlatformName = lastPlatformName;
        this.isSafe = isSafe;
    }

    get isOnPlatform() {
        return this.platformName !== null;
    }

    get isNewPlatform() {
        return this.platformName !== null && this.platformName !== this.lastPlatformName;
    }
}

class CutUndo {
    levelJson: any;
    cutJson: any;

    constructor(levelJson: any, cutJson: any) {
        this.levelJson = levelJson;
        this.cutJson = cutJson;
    }
}

// Not really an undo, just the initial level definition
class InitialLevelUndo {
    levelJson: any;

    constructor(json: any) {
        this.levelJson = json;
    }
}

class UndoState {
    initialLevel: InitialLevelUndo;
    undoStack: (CutUndo|PlayerMoveUndo)[]
    numberOfCuts: number;
    updatesSinceLastEvent: number;
    lastPlayersPlatformName: string | null;

    constructor(levelJson: any) {
        this.initialLevel = new InitialLevelUndo(levelJson);
        this.undoStack = [];
        this.numberOfCuts = 0;
        this.updatesSinceLastEvent = 0;
        this.lastPlayersPlatformName = null;
    }

    getLastUndo(): PlayerMoveUndo | CutUndo | InitialLevelUndo {
        let undo = this.undoStack.pop();

        if(undo instanceof CutUndo) this.numberOfCuts--;

        return undo || this.initialLevel;
    }

    getLastHardUndo(): CutUndo | InitialLevelUndo {
        while(true) {
            let undo = this.getLastUndo();
            if(undo instanceof CutUndo || undo instanceof InitialLevelUndo) return undo;
        }
    }

    getInitialLevelJson(): any {
        return this.initialLevel.levelJson;
    }

    getNumberOfCuts(): number {
        return this.numberOfCuts;
    }

    peekLast<T>(type: Function, count: number = 1): T | undefined {
        function isT(arg: any): arg is T {
            return arg instanceof type;
        }

        for(let i = this.undoStack.length-1; i >= 0; i--) {
            let event = this.undoStack[i];
            if(isT(event) && --count == 0) {
                return event;
            }
        }

        return undefined;
    }

    push(undo: PlayerMoveUndo | CutUndo) {
        if(undo instanceof CutUndo) this.numberOfCuts++;

        this.undoStack.push(undo);
    }

    update(playerJson: any, playersPlatform: platform.Platform | undefined,
           newPlatformCallback: (name: string, lastName: string) => void) {
        this.updatesSinceLastEvent++;

        let lastMoveEvent = this.peekLast<PlayerMoveUndo>(PlayerMoveUndo);
        let lastPlatformName = !lastMoveEvent ? null :
            lastMoveEvent.platformName || lastMoveEvent.lastPlatformName;
        let curPlatformName = (playersPlatform !== undefined) ? playersPlatform.name : null;

        if(curPlatformName !== null && curPlatformName !== lastPlatformName) {
            // call the new platform callback
            newPlatformCallback(curPlatformName, lastPlatformName || "null");

            // and definitely record this new platform events
        } else if (this.updatesSinceLastEvent < 3) {
            // Ignore every third update, so we rewind at thrice the speed
            return;
        } else if(Math.abs(playerJson.vx) + Math.abs(playerJson.vy) < 0.5) {
            // Ignore updates where the player is not moving
            // Unless this position is safe and the last one was either not safe, didn't exist (level start), or was a cut
            let last = this.peekLast<PlayerMoveUndo>(PlayerMoveUndo);
            if(curPlatformName != null && (last === undefined || !last.isSafe || last !== this.undoStack[this.undoStack.length-1] )) {
                this.undoStack.push(new PlayerMoveUndo(playerJson, curPlatformName, lastPlatformName, true));
                this.updatesSinceLastEvent = 0;
            }
            return;
        } else {
            // by default, record this update
        }

        // Create and push the undo event
        this.undoStack.push(new PlayerMoveUndo(playerJson, curPlatformName, lastPlatformName, false));
        this.updatesSinceLastEvent = 0;

        // If we are not moving in the y direction this frame and our previous player
        // position (from two undos ago) seemed safe, then it must be.
        let last = this.peekLast<PlayerMoveUndo>(PlayerMoveUndo, 2);
        if(curPlatformName != null && Math.abs(playerJson.vy) < 2
            && last !== undefined && last.isOnPlatform && Math.abs(last.playerJson.vy) < 2) {
            last.isSafe = true;
        }
    }

    addCut(levelJson: any, cutJson: any) {
        this.numberOfCuts++;
        this.undoStack.push(new CutUndo(levelJson, cutJson));
    }
}

/**
 * The game state for a level.
 */
export default class LevelState extends Phaser.State {
    platforms: platform.Platform[];
    player: player.Player;
    goal: goal.Goal;
    lasers: laser.Laser[];
    laserCollection: laser.LaserCollection;

    objectByName: {[key: string]: platform.Platform | laser.Laser};

    bgGroup: Phaser.Group;
    cutUiGroup: Phaser.Group;
    uiGroup: Phaser.Group;
    overlayGroup: Phaser.Group;
    platformGroup: Phaser.Group;
    gameObjectGroup: Phaser.Group;

    playerCollisionGroup: Phaser.Physics.P2.CollisionGroup;
    platformCollisionGroup: Phaser.Physics.P2.CollisionGroup;
    spikedCollisionGroup: Phaser.Physics.P2.CollisionGroup;

    cutState: cutandslide.CutState;
    lvlLabel: Phaser.BitmapText | Phaser.Text;
    cutLabel: Phaser.BitmapText | Phaser.Text;
    parLabel: Phaser.BitmapText | Phaser.Text;
    par: number;

    undoState: UndoState;

    background: Phaser.Sprite;

    tutorial: tutorial.Tutorial;
    overlay: overlay.Overlay;
    dashboard: dashboard.Dashboard;
    dashboardOverlay: Phaser.Graphics;

    isLevelCompleted: boolean;
    // omae wa mou shindeiru yo
    isAlreadyDead: boolean;

    isCurrentlyCutResetting: boolean; // True if we should be rewinding to before the last cut
    isCurrentlySoftResetting: boolean; // True if we should be rewinding to last safe position
    undoCutSprite: Phaser.Sprite | null = null;

    muteButton: MuteButton;
    fogBackground: Fog;

    static startWithLevelName(stateManager: Phaser.StateManager, levelName: string) {
        let json = Level.getLevelJson(levelName);
        let index = progression.LEVEL_ID_MAP[levelName];
        progression.syncToLevel(levelName);
        logging.startLevel(index, {
            "name": levelName,
            "relativeIndex": progression.currentLevelIndex,
        });
        stateManager.start("level", true, false, json);
    }

    init(levelJson: any): void {
        this.undoState = new UndoState(levelJson);
        // Workaround: fixes the mousedown handler that isn't firing,
        // and lets us process left and right mouse button at the same
        // time
        this.game.input.mspointer.stop();
    }

    create() {
        AudioEngine.get().switchTo("regular");

        this.stage.disableVisibilityChange = true;

        this.game.stage.backgroundColor = "#FFFFFF";

        this.bgGroup = this.game.add.group();
        this.gameObjectGroup = this.game.add.group();
        this.platformGroup = this.game.add.group(this.gameObjectGroup);
        this.cutUiGroup = this.game.add.group();
        this.uiGroup = this.game.add.group();
        this.overlayGroup = this.game.add.group();

        this.game.add.tween(this.uiGroup)
            .from({ alpha: 0 }, 300, Phaser.Easing.Quadratic.Out, true);

        this.platforms = [];
        this.lasers = [];
        this.objectByName = {};

        const cutSprite = this.game.add.sprite(0, 0, "cut", null, this.cutUiGroup);
        this.cutState = new cutandslide.CutState(
            this.gameObjectGroup,
            cutSprite,
            this.game.add.graphics(0, 0, this.cutUiGroup),
            this.game.add.graphics(0, 0, this.cutUiGroup),
            {
                x: 0,
                y: 0,
                width: this.game.width,
                height: this.game.height - 60,
            },
            this.serializeMutableState.bind(this),
        );

        this.cutState.onCutFinish.add((result: cutandslide.CutFinishEvent) => {
            cursor.instance().defaultCursor = "default";
            if (result.commit) {
                this.undoState.addCut(this.serializeMutableState(), result.state);
            }
            this.laserCollection.resetOffsets();
        });

        this.dashboardOverlay = this.game.add.graphics(0, 0, this.uiGroup);
        this.dashboardOverlay.beginFill(0x000000);
        this.dashboardOverlay.drawRect(0, 540, this.game.width, 60);
        this.dashboardOverlay.alpha = 0.5;

        this.lvlLabel = i18n.label(this.game, 16, 570, "", "font-small", 32);
        this.uiGroup.add(this.lvlLabel);
        this.lvlLabel.anchor.setTo(0,0.5);
        this.lvlLabel.text = i18n._("LEVEL", {
            level: progression.currentLevelIndex + 1
        });

        this.cutLabel = i18n.label(this.game, 500, 570, "", "font-small", 32);
        this.uiGroup.add(this.cutLabel);
        this.cutLabel.anchor.setTo(0, 0.5);
        this.cutLabel.alpha = 1;

        this.parLabel = i18n.label(this.game, 784, 570, "", "font-small", 32);
        this.uiGroup.add(this.parLabel);
        this.parLabel.anchor.setTo(1,0.5);
        this.parLabel.alpha = 1;

        if (!logging.PRODUCTION) {
            this.uiGroup.add(this.cutState.debugState);
        }

        // P2 physics
        this.game.physics.startSystem(Phaser.Physics.P2JS);
        this.game.physics.p2.setImpactEvents(true);
        this.game.physics.p2.gravity.y = 2100;
        this.game.physics.p2.setBounds(0, 0, this.game.width, this.game.height, true, true, true, false);
        // create collision groups & materials
        this.playerCollisionGroup = this.game.physics.p2.createCollisionGroup();
        this.platformCollisionGroup = this.game.physics.p2.createCollisionGroup();
        this.spikedCollisionGroup = this.game.physics.p2.createCollisionGroup();

        // makes sure things in new groups still collide with world bounds
        this.game.physics.p2.updateBoundsCollisionGroup();

        // Load the level
        // Populates this.platforms, this.goal, and this.player
        this.loadFromJson(this.undoState.getInitialLevelJson(), assets);

        // platform material (for collide with player)
        const platMat = this.game.physics.p2.createMaterial('platMat');

        for (let p of this.platforms) {
            if (p.attrs.spiked) {
                p.physicsBody.setCollisionGroup(this.spikedCollisionGroup);
                if (p instanceof platform.TiledPlatform) {
                    // p.baseFilter = [new platform.TintFilter(
                    //     this.game,
                    //     5.0, 2.4, 0.0, 0.5,
                    // )];
                    p.redraw();
                }
            }
            else {
                p.physicsBody.setCollisionGroup(this.platformCollisionGroup);
            }
            p.physicsBody.collides(this.playerCollisionGroup);
            p.physicsBody.setMaterial(platMat);
            this.platformGroup.add(p.displayObject);
        }

        for (let l of this.lasers) {
            l.physicsBody.setCollisionGroup(this.platformCollisionGroup);
            l.physicsBody.collides(this.playerCollisionGroup);
            l.physicsBody.setMaterial(platMat);
        }

        // player material (for collide with platform)
        const playMat = this.game.physics.p2.createMaterial('playMat', this.player.body);

        // platform and player contact material (used for calculating collisions)
        const platPlayCM = this.game.physics.p2.createContactMaterial(playMat, platMat);
        // collide more exactly with platform
        platPlayCM.stiffness = 1e10;
        platPlayCM.friction = 0.0;

        // makes sure things in new groups still collide with world bounds
        this.game.physics.p2.updateBoundsCollisionGroup();

        this.game.input.onTap.add((pointer: Phaser.Pointer, doubleTap: boolean) => {
            if (doubleTap) {
                this.cutState.tryFinish();
            }
            else {
                this.cutState.updateClick(pointer);
            }
        });

        this.platforms.forEach((p) => this.cutState.addObject(p));
        this.cutState.addObject(this.player);
        this.cutState.addObject(this.goal);
        this.lasers.forEach((l) => this.cutState.addObject(l));
        this.laserCollection = new laser.LaserCollection(this.platforms, this.lasers);

        this.game.input.keyboard.addCallbacks(null, undefined, undefined, (e: string) => {
            if (e === "r") {
                this.hardReset();
            }
            else if (e === "c") {
                logging.logAction(logging.Action.CancelCut);
                this.cutState.cancel();
            }
        });

        const tryJump = (key: string, finishSlide: boolean) => () => {
            if (!this.cutState.isCutting) {
                this.player.tryJump = true;
                this.player.jumpKey = key;
            }
            else if (finishSlide) {
                this.cutState.tryFinish();
            }
        };

        this.game.input.keyboard.addKey(Phaser.KeyCode.W).onDown.add(tryJump("W", false));
        this.game.input.keyboard.addKey(Phaser.KeyCode.UP).onDown.add(tryJump("Up", false));
        this.game.input.keyboard.addKey(Phaser.KeyCode.SPACEBAR).onDown.add(tryJump("Spacebar", true));

        this.isCurrentlySoftResetting = false;
        this.isCurrentlyCutResetting = false;

        this.tutorial = new tutorial.Tutorial(this.game, this.uiGroup,
            this.undoState.getInitialLevelJson().name, this.cutState);
        this.overlay = new overlay.Overlay(this.game, this.overlayGroup);
        this.dashboard = new dashboard.Dashboard(this.game, this.uiGroup);
        this.dashboard.undoButton.addCallback(() => { this.undo() });
        this.dashboard.restartButton.addCallback(() => { this.hardReset() });
        this.dashboard.menuButton.addCallback(() => { this.levelselect() });

        this.isLevelCompleted = false;
        this.isAlreadyDead = false;

        let levelJSON = localStorage.getItem("levels");
        if(levelJSON != null){
            let levelData = JSON.parse(levelJSON);
            let locked = levelData[progression.currentLevelIndex] ?
                levelData[progression.currentLevelIndex].locked : true;
            if(locked){
                levelData[progression.currentLevelIndex] = {locked: false, par:100};
            }
            localStorage.setItem("levels", JSON.stringify(levelData));
        }
        else {
            console.log("Please enable localStorage to save game progress.");
        }

        this.muteButton = new MuteButton(this.game, () =>{
            AudioEngine.get().mute();
        },
        () => {
            AudioEngine.get().unmute();
        }, 0);

        // Reset keyboard when we lose focus (so keypresses do not get "stuck")
        this.game.onBlur.add(() => {
            this.game.input.keyboard.reset(false);
        });

        // Stop all sounds when the game pauses
        this.game.onBlur.add(() => { AudioEngine.get().stopAllSounds(); });
        this.transitionIn();
    }

    levelselect() {
        // Stop all game level sounds
        AudioEngine.get().stopAllSounds();

        //TODO: Log action for switch level
        this.state.add("level-select", LevelSelectState);
        this.state.start("level-select");
    }

    render() {
    }

    reset() {
        cursor.instance().defaultCursor = "default";
        this.isCurrentlySoftResetting = true;
        this.isAlreadyDead = false;
        this.laserCollection.resetOffsets();
        this.cutState.cancel();
        this.player.undoDeath();
    }

    hardReset() {
        if (this.isLevelCompleted) return;
        logging.logAction(logging.Action.Reset);
        cursor.instance().defaultCursor = "default";
        this.state.restart(true, false, this.undoState.initialLevel.levelJson);
    }

    shutdown() {
        this.game.input.mouse.releasePointerLock();
    }

    undo() {
        logging.logAction(logging.Action.UndoButton);
        this.cutState.cancel();
        this.isCurrentlyCutResetting = true;
    }

    setupPlayerCollisions(player: player.Player) {
        player.body.setCollisionGroup(this.playerCollisionGroup);
        player.body.collides(this.platformCollisionGroup, (play: any, plat: any) => {
            player.onHitPlatform();
        });
        player.body.collides(this.spikedCollisionGroup, (play: any, plat:any) => {
            // Kill the player if colliding with the top of spiked platform

            // Unless we are rewinding
            if(this.isCurrentlySoftResetting) {
                return;
            }

            let culpritName;
            let culpritX;
            let culpritY;

            for (const platform of this.platforms){
                if (platform.physicsBody == plat){
                    culpritName = platform.name;
                    culpritX = platform.physicsBody.x;
                    culpritY = platform.physicsBody.y;
                }
            }

            let data = {
                "player": {
                    "x": player.body.x,
                    "y": player.body.y,
                },
                "platform": {
                    "name": culpritName,
                    "x": culpritX,
                    "y": culpritY,
                }
            };

            if (player.canJump()) {
                if (!this.isAlreadyDead && !this.isLevelCompleted) {
                    AudioEngine.get().play("hurt");
                    logging.logAction(logging.Action.SpikeDeath, data);
                    player.playDeathAnimation(() => {
                        this.overlay.death(i18n._("Impaled By Spikes"), () => { this.reset(); });
                    });
                }
                this.isAlreadyDead = true;
            }
        });
    }

    update() {
        // Forced level switch
        if (!logging.PRODUCTION && this.game.input.keyboard.isDown(Phaser.KeyCode.ENTER)) {
            if (!this.isLevelCompleted) {
                this.levelCompleted("", false);
                this.overlay.victorySkip();
            }
            return;
        }

        // Check if we are in the middle of animating a cut undo, and wait until that is done
        if(this.undoCutSprite !== null) {
            return;
        }

        // Handle a soft undo/rewind
        if (this.isCurrentlySoftResetting || this.isCurrentlyCutResetting ||
            !logging.PRODUCTION && this.game.input.keyboard.isDown(Phaser.KeyCode.BACKSPACE)) {

            // Get the last undo, and then perform the undo (based on the type)
            let undo = this.undoState.getLastUndo();

            if(undo instanceof PlayerMoveUndo) {
                this.player.deserializeMutable(undo.playerJson);
                if(this.isCurrentlySoftResetting) {
                    if(undo.isSafe) {
                        // Cancel any cut the player may have madeSlide
                        this.cutState.cancel();

                        this.isCurrentlySoftResetting = false;

                        // Cancel out any player velocity (such as the player's lethal jump)
                        this.player.body.velocity.x = 0;
                        this.player.body.velocity.y = 0;
                    }
                }
            } else if (undo instanceof CutUndo) {
                // Get cut data (handling case of both sliding or cut being serialized)
                let cut = undo.cutJson.statusData.cut ? undo.cutJson.statusData.cut : undo.cutJson.statusData;
                let cutUndo = undo;

                logging.logAction(logging.Action.UndoCut, { cutData: cut, cutCount: this.undoState.getNumberOfCuts() });

                this.undoCutSprite = this.game.add.sprite(0, 0, "cut", null, undefined);
                this.undoCutSprite.pivot.set(0.5, 0);
                this.undoCutSprite.anchor.set(0.5, 0);
                let [
                    { x: startX, y: startY },
                    { x: endX, y: endY },
                    angle
                ] = cutandslide.CutState.prototype.positionLine.call(this, cut.lineStart, cut.lineSlope);

                if (startY <= endY) {
                    this.undoCutSprite.position.setTo(startX, startY);
                }
                else {
                    this.undoCutSprite.position.setTo(endX, endY);
                }
                this.undoCutSprite.visible = true;
                this.undoCutSprite.rotation = angle;
                this.undoCutSprite.height = Math.sqrt((endX - startX)**2 + (endY - startY)**2);
                this.undoCutSprite.alpha = 0.0;

                let tween1 = this.game.add.tween(this.undoCutSprite).to({alpha: 1.0}, 200, "Linear");
                tween1.onComplete.add(() => {
                    this.deserializeMutable(cutUndo.levelJson);
                    if(this.isCurrentlyCutResetting) {
                        this.isCurrentlyCutResetting = false;
                        this.isCurrentlySoftResetting = true;
                    }

                    // If the last player position was safe, then we are still safe now
                    let lastPlayerUndo = this.undoState.peekLast<PlayerMoveUndo>(PlayerMoveUndo);
                    if(lastPlayerUndo === undefined || lastPlayerUndo.isSafe) {
                        this.isCurrentlySoftResetting = false;
                    }

                    let tween2 = this.game.add.tween(this.undoCutSprite).to({alpha: 0.0}, 200, "Linear");
                    tween2.onComplete.add(() => {
                        if(this.undoCutSprite !== null) this.undoCutSprite.destroy();
                        this.undoCutSprite = null;
                        this.game.physics.p2.paused = false;
                    });
                    tween2.start();
                });
                tween1.start();
                this.game.physics.p2.paused = true;
            } else if (undo instanceof InitialLevelUndo) {
                this.deserialize(undo.levelJson);

                this.isCurrentlyCutResetting = false;
                this.isCurrentlySoftResetting = false;
            }
            return;
        }

        //restart if offscreen
        //probably needs more detailed death animation/notification.
        if (this.player.isDead() && !this.isLevelCompleted) {
            if (!this.isAlreadyDead) {
                let loc = {
                    "x" : this.player.body.x,
                    "y" : this.player.body.y
                };

                AudioEngine.get().play("hurt_echo");
                logging.logAction(logging.Action.FallingDeath, loc);
                this.overlay.death(i18n._("Plummeted To Death"), () => { this.reset(); });
            }

            this.isAlreadyDead = true;
        }

        const laser = this.laserCollection.update(
            this.player,
            this.cutState.offsetFor.bind(this.cutState));
        if (laser) {
            if (!this.isAlreadyDead && !this.isLevelCompleted) {
                let data = {
                    "player": {
                        "x": this.player.body.x,
                        "y": this.player.body.y,
                    },
                    "laser": {
                        "name": laser.name,
                        "x": laser.emitter.x,
                        "y": laser.emitter.y,
                    }
                };

                AudioEngine.get().play("hurt");
                AudioEngine.get().play("laser");
                AudioEngine.get().stop("walking");
                logging.logAction(logging.Action.LaserDeath, data);
                this.player.playDeathAnimation();
                this.overlay.death(i18n._("Vaporized By Laser"), () => { this.reset(); });
            }
            this.isAlreadyDead = true;
        }

        if (!this.isAlreadyDead) {
            this.player.update(this.cutState.isCutting, this.isLevelCompleted, (key: string | null) => {
                let curPlatform = this.player.getCurrentPlatformFrom(this.platforms);
                logging.logAction(logging.Action.Jump, {
                    platform: curPlatform === undefined ? "null" : curPlatform.name,
                    player: this.player.serializeMutable(),
                    key: key,
                })
            });
        }


        if (this.goal.reachedGoal(this.player.aabb) && !this.player.isDead()) {
            let message = "On Par";
            if (this.cutCount < this.par) {
                message = "Under Par";
            }
            else if (this.cutCount > this.par) {
                message = "Over Par";
            }
            this.levelCompleted(i18n._(message, {
                cutCount: Math.abs(this.cutCount - this.par),
            }), this.cutCount <= this.par);
            return;
        }

        if (!this.player.isDead()) {
            if (this.game.input.activePointer.rightButton.isDown) {
                this.cutState.cancel();
            }
            this.cutState.update(
                this.game.input.activePointer.leftButton.isDown,
                this.game.input.activePointer.x, this.game.input.activePointer.y,
                this.game.input.mouse.event ?
                    this.game.input.mouse.event.movementX : 0,
                this.game.input.mouse.event ?
                    this.game.input.mouse.event.movementY : 0,
            );
            (this.game.input.mouse as any).event = null;
            let cutCount = this.cutCount;
            if(this.parLabel.text !== "") {
                this.cutLabel.text = i18n._("NUM_CUTS", {
                    cuts: this.undoState.getNumberOfCuts(),
                });
            } else {
                this.cutLabel.text = "";
            }

            if (cutCount < this.par) {
                // white under par
                this.cutLabel.tint = 0xFFFFFF;
            }
            else if (cutCount === this.par) {
                // green at par
                this.cutLabel.tint = 0x28b712;
            }
            else if (cutCount >= 3 * this.par) {
                // red if triple par
                this.cutLabel.tint = 0xd83722;
            }
            else {
                // orange if over, but less than 3x
                this.cutLabel.tint = 0xf4a442;
            }
        }
        else {
            this.cutState.flagDead();
        }

        this.game.physics.p2.paused =
            (this.cutState.physicsPaused &&
             !this.player.overridePhysicsPaused) ||
            this.player.physicsPaused;

        // Record players movement, but only if we are not dead and physics is
        // not paused
        if(!this.isAlreadyDead && !this.cutState.physicsPaused) {
            this.undoState.update(this.player.serializeMutable(),
                this.player.getCurrentPlatformFrom(this.platforms),
                (curPlatName: string, lastPlatName: string) => {
                    logging.logAction(logging.Action.NewPlatform, {
                        "last": lastPlatName,
                        "cur": curPlatName,
                        "player": this.player.serializeMutable(),
                    })
                });
        }

        if (this.cutState.status instanceof cutandslide.Sliding ||
            this.cutState.status instanceof cutandslide.Cut ||
            this.cutState.status instanceof cutandslide.Animating) {
            this.background.alpha = this.cutState.bgAlpha;
        }
        else {
            this.background.alpha = 1.0;
        }

        // Update tutorial messages
        this.tutorial.update(this.player);
        cursor.instance().update();
    }

    serializeState(): any {
        return {
            platforms: this.platforms.reduce((platforms:{[key: string]: platform.Platform}, p) => {
                platforms[p.name] = p.serialize();
                return platforms;
            }, {}),
            goal: this.goal.serialize(),
            player: this.player.serialize(),
            obstacles: this.lasers.reduce((obstacles:{[key: string]: laser.Laser}, l) => {
                obstacles[l.name] = l.serialize();
                return obstacles;
            }, {}),
            cutCount: this.cutCount,
        };
    }

    serializeMutableState(): any {
        return {
            platforms: this.platforms.reduce((platforms:{[key: string]: platform.Platform}, p) => {
                platforms[p.name] = p.serializeMutable();
                return platforms;
            }, {}),
            goal: this.goal.serializeMutable(),
            player: this.player.serializeMutable(),
            obstacles: this.lasers.reduce((obstacles:{[key: string]: laser.Laser}, l) => {
                obstacles[l.name] = l.serializeMutable();
                return obstacles;
            }, {}),
            cutCount: this.cutCount,
        };
    }

    levelCompleted(message: string, onPar: boolean) {
        // Only trigger first time level is completed
        if (!this.isLevelCompleted) {
            // Disable UI
            this.dashboard.menuButton.button.inputEnabled = false;
            this.dashboard.undoButton.button.inputEnabled = false;
            this.dashboard.restartButton.button.inputEnabled = false;
            this.game.add.tween(this.uiGroup)
                .to({ alpha: 0 }, 300, Phaser.Easing.Quadratic.Out, true)
                .onComplete.add(() => { this.uiGroup.visible = false; });

            let isFirstLevel = progression.currentLevelIndex === 0;

            // Triggers logging event, and plays victory screen (which will switch
            // to a new level after an animation)
            let levelJSON = localStorage.getItem("levels");
            if(levelJSON != null){
                let levelData = JSON.parse(levelJSON);
                let oldPar = levelData[progression.currentLevelIndex].par;
                if(oldPar > this.cutCount - this.par){
                    levelData[progression.currentLevelIndex] = {locked: false, par:(this.cutCount - this.par)};
                }
                if(isFirstLevel) {
                    levelData[progression.currentLevelIndex] = {locked: false, par:0};
                }
                localStorage.setItem("levels", JSON.stringify(levelData));
            }
            else {
                console.log("Please enable localStorage to save game progress.");
            }

            logging.logAction(logging.Action.Finish, {
                cutCount: this.cutCount,
                par: this.par,
            });
            logging.finishLevel();
            let gs = this.game.state;

            this.goal.beamUp(this.player);

            if(isFirstLevel) {
                // Don't give par message on first level
                message = "";
                onPar = true;
            }

            if(onPar) {
                // Play the victory sound
                AudioEngine.get().play("collect_star");
            }

            this.overlay.victory(message, onPar ? this.goal.detachStar() : null, () => {
                // Stop all game level sounds
                AudioEngine.get().stopAllSounds(["collect_star"]);

                this.transitionOut().onComplete.add(() => {
                    if (progression.isFinished()) {
                        this.game.state.start("congratulations");
                    }
                    else {
                        LevelState.startWithLevelName(gs, progression.nextLevel());
                    }
                });
            });
        }

        this.isLevelCompleted = true;
    }

    loadFromJson(json: any, assetStyles: platform.AssetStyleCollection) {
        const levelScale = json.scale || 1;

        const bgChoice = bgForLevel(progression.currentLevelIndex);
        const bg = this.game.add.sprite(0, 0, bgChoice);
        bg.width = this.game.width;
        bg.height = this.game.height;
        this.background = bg;
        this.bgGroup.add(bg);

        this.fogBackground = new Fog(this.game, this.bgGroup);

        // Load the platforms
        for (const p of json.platforms) {
            if (!p.message) {
                console.warn("Platform is missing a message:", p);
            }

            // Make sure messages are unique
            if (this.objectByName[p.message] !== undefined) {
                console.warn("Message is duplicated:", p.message);
            }

            switch (p.type) {
            case "FiniteImmobilePlatform":
                let plat = new platform.FiniteImmobilePlatform(this.game, p, assetStyles, levelScale);
                this.platforms.push(plat);
                this.objectByName[p.message] = plat;
                break;
            default:
                throw new Error(`Unrecognized platform type '${p.type}'`);
            }
        }

        if (json.obstacles) {
            for (const obstacle of json.obstacles) {
                if (!obstacle.message) {
                    console.warn("Obstacle is missing a message:", obstacle);
                }

                // Make sure messages are unique
                if (this.objectByName[obstacle.message] !== undefined) {
                    console.warn("Message is duplicated:", obstacle.message);
                }

                switch (obstacle.type) {
                case "laser":
                    let laz = new laser.Laser(this.game, obstacle, levelScale);
                    this.lasers.push(laz);
                    this.objectByName[obstacle.message] = laz;
                    break;
                }
            }
        }

        if (json.par) {
            this.parLabel.text = i18n._("PAR", {
                par: json.par,
            });
        }
        else {
            this.parLabel.text = "";
        }

        this.par = json.par;

        // Load the goal
        this.goal = new goal.Goal(this.game, json.goal, levelScale);

        // Load the player
        this.player = new player.Player(this.game, json.player, this.setupPlayerCollisions.bind(this), levelScale);

        for (const platform of this.platforms) {
            this.platformGroup.add(platform.displayObject);
        }

        this.gameObjectGroup.add(this.goal.displayObject);
        this.gameObjectGroup.add(this.player.displayObject);
        this.gameObjectGroup.addMultiple(this.lasers.map(x => x.displayObject));
    }

    deserialize(json: any): void {
        this.player.deserializeMutable(json.player, json.scale);
        this.goal.deserializeMutable(json.goal, json.scale);

        for(let p of json.platforms) {
            this.objectByName[p.message].deserializeMutable(p, json.scale);
        }

        for(let l of json.obstacles || []) {
            this.objectByName[l.message].deserializeMutable(l, json.scale);
        }
    }

    deserializeMutable(json: any): void {
        this.player.deserializeMutable(json.player, json.scale);
        this.goal.deserializeMutable(json.goal, json.scale);

        for(let name in json.platforms) {
            this.objectByName[name].deserializeMutable(json.platforms[name]);
        }

        for(let name in json.obstacles) {
            this.objectByName[name].deserializeMutable(json.obstacles[name]);
        }
    }

    get cutCount(): number {
        return this.undoState.getNumberOfCuts();
    }

    transitionOut(): Phaser.Tween {
        const target = {
            alpha: 0.0,
            y: this.game.height,
        };
        const tween = this.game.add.tween(this.gameObjectGroup).to(
            target, 500, Phaser.Easing.Cubic.Out).start();

        let nextBg = "background7";
        if (!progression.isFinished()) {
            nextBg = bgForLevel(progression.currentLevelIndex + 1);
        }
        if (progression.challengesFinished()) {
            nextBg = "background8";
        }

        if (nextBg != bgForLevel(progression.currentLevelIndex)) {
            this.game.stage.backgroundColor = "#000000";
            const newBg = this.game.add.sprite(0, 0, nextBg);
            newBg.width = this.game.width;
            newBg.height = this.game.height;
            newBg.alpha = 0.0;
            this.bgGroup.add(newBg);

            this.game.add.tween(this.background).to({
                alpha: 0.0,
            }, 500, Phaser.Easing.Cubic.Out).start();
            this.game.add.tween(newBg).to({
                alpha: 1.0,
            }, 500, Phaser.Easing.Cubic.Out).start();
        }

        return tween;
    }

    transitionIn(): Phaser.Tween {
        const tween = this.game.add.tween(this.gameObjectGroup).from({
            alpha: 0.0,
            y: -this.game.height,
        }, 500, Phaser.Easing.Cubic.Out).start();
        return tween;
    }
}



// WEBPACK FOOTER //
// ./src/states/level.ts